import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import PropTypes from 'prop-types';

import { CREATE_WORKSPACE_PROFILE, GET_WORKSPACE } from '../../../../apis/workspace';
import { navigateToWorkspaceUrl } from '../../../../utils/navigator';
import { ONBOARDING } from '../../../../constants/routes';
import { uploadWorkspaceProfileAvatar } from '../../../../utils/upload-avatar';
import Button from '../../../../components/atoms/button';
import Input from '../../../../components/atoms/input';
import UploadAvatarCard from '../../../../components/organisms/upload-avatar-card';
import useAuth from '../../../../context/AuthContext';
import useDebounce from '../../../../utils/debounce';
import useWorkspaceInvite from '../../../../context/WorkspaceInviteContext';

const InviteUserCompleteWorkspaceProfileProps = {
  setIsLoading: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
};

const InviteUserCompleteWorkspaceProfile = (props) => {
  // init
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsInviteUser } = useWorkspaceInvite();
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const { token } = useWorkspaceInvite();
  const { setToast, setIsLoading } = props;

  // states
  const [workspaceProfile, setWorkspaceProfile] = useState(undefined);
  const [workspaceProfileLogo, setWorkspaceProfileLogo] = useState({ blob: undefined, dataURI: undefined, file: undefined });
  const [workspaceProfileName, setWorkspaceProfileName] = useState('');
  const [isProfileNameValid, setIsProfileNameValid] = useState(true);
  const [debouncedWorkspaceProfileName] = useDebounce(workspaceProfileName, 500);

  // mutations/queries
  const [createWorkspaceProfile] = useMutation(CREATE_WORKSPACE_PROFILE);
  const [getWorkspace] = useLazyQuery(GET_WORKSPACE, { fetchPolicy: 'network-only' });

  const handleWorkspaceProfileNameChange = (event) => {
    setIsProfileNameValid(!!event.target.value);
    setWorkspaceProfileName(event.target.value);
  };

  const handleAvatarRemove = () => {
    setWorkspaceProfileLogo({ blob: undefined, dataURI: undefined, file: undefined });
  };

  const handleFinish = async () => {
    try {
      setIsLoading(true);
      const variables = {
        input: {
          token,
          isActive: true,
          displayName: workspaceProfileName,
        },
      };
      let newWorkspaceProfile = workspaceProfile;
      if (!workspaceProfile?.id) {
        const { data: { createWorkspaceProfile: workspaceProfileData } } = await createWorkspaceProfile({ variables });
        setWorkspaceProfile(workspaceProfileData);
        newWorkspaceProfile = workspaceProfileData;
      }
      await uploadWorkspaceProfileAvatar({ userId: newWorkspaceProfile.userId, workspaceProfileId: newWorkspaceProfile.id, workspaceProfileLogo, workspaceId: newWorkspaceProfile.workspaceId });
      const { data: { getWorkspace: workspace } } = await getWorkspace({ variables: { input: { id: newWorkspaceProfile.workspaceId } } });
      if (!isLoggedIn) setIsLoggedIn(true);
      navigateToWorkspaceUrl(workspace.handle);
    } catch (error) {
      setToast({ open: true, message: error?.message || 'Something went wrong!', autoHideDuration: 3000, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(`/${ONBOARDING}`);
    setIsInviteUser(false);
  };

  return (
    <>
      <div>
        <h2 style={{ margin: '0px 0px 20px 0px' }} className="step-title">Complete your profile</h2>
        <div className="border-cards">
          <div className="border-card">
            <span className="border-card-title">How do you want your name to appear?</span>
            <div className="border-card-input-container">
              <span className="border-card-label">Full name</span>
              <Input error={!isProfileNameValid} helperText="Profile name is required!" autoFocus placeholder="Enter full name" onChange={handleWorkspaceProfileNameChange} value={workspaceProfileName} />
            </div>
          </div>
          {/* Workspace Profile Logo */}
          <UploadAvatarCard name={debouncedWorkspaceProfileName} title="Upload your profile photo." uploadButtonText="Upload" isOptional avatar={workspaceProfileLogo.dataURI} onUpload={setWorkspaceProfileLogo} onRemove={handleAvatarRemove} />
        </div>
      </div>
      <div style={{ display: 'flex', alignSelf: 'end', gap: 10 }}>
        {isLoggedIn && searchParams.get('email') && <Button primary outlined onClick={handleGoBack}>Back</Button>}
        <Button onClick={handleFinish} primary>Finish</Button>
      </div>
    </>
  );
};

InviteUserCompleteWorkspaceProfile.propTypes = InviteUserCompleteWorkspaceProfileProps;

export default InviteUserCompleteWorkspaceProfile;
